/**
 * Turning the cart into a quote.
 *
 * The buttons here mirror Tools > Customer Order Flow w ERP: emailing, saving
 * without emailing, quoting a visitor who is not signed in, and inviting them
 * to sign in first are each switched on or off there. What the screen does not
 * offer, the endpoint refuses too, so this is presentation rather than the
 * enforcement.
 */
import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { Cart } from "../common/models/cart";
import {
  Quote,
  QuoteFlowConfig,
  downloadQuotePdf,
  emailQuote,
  messageOf,
  reviseQuote,
  saveQuote,
} from "./api";

export type QuoteDialogProps = {
  open: boolean;
  onClose: () => void;
  cart: Cart;
  config: QuoteFlowConfig;
  signedIn: boolean;
  defaults: { name: string; company: string; email: string; phone: string };
  /** Set when the cart came from a saved quote, so saving raises a revision. */
  revisingQuoteId?: string | null;
  onSaved?: (quote: Quote) => void;
};

const field =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#00062e] focus:outline-none";

const money = (amount: number, currency: string) =>
  `${currency === "USD" ? "$" : `${currency} `}${amount.toLocaleString(
    "en-US",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  )}`;

const day = (iso: string) => (iso ? iso.slice(0, 10) : "");

export default function QuoteDialog({
  open,
  onClose,
  cart,
  config,
  signedIn,
  defaults,
  revisingQuoteId,
  onSaved,
}: QuoteDialogProps) {
  const [name, setName] = useState(defaults.name);
  const [company, setCompany] = useState(defaults.company);
  const [email, setEmail] = useState(defaults.email);
  const [phone, setPhone] = useState(defaults.phone);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [quote, setQuote] = useState<Quote | null>(null);
  const [emailed, setEmailed] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(defaults.name);
    setCompany(defaults.company);
    setEmail(defaults.email);
    setPhone(defaults.phone);
    setQuote(null);
    setEmailed("");
    setError("");
    // Only when the dialog opens: retyping the customer's own edits away while
    // they are looking at the form would be its own bug.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const quoting = config.quote;
  const blockedAsGuest = !signedIn && !quoting.allowGuests;

  const save = async (deliver: boolean) => {
    setError("");
    setBusy(deliver ? "email" : "save");
    try {
      const customer = { name, company, email, phone };
      const saved = revisingQuoteId
        ? await reviseQuote(revisingQuoteId, { cart, customer })
        : await saveQuote({ cart, customer, deliver });
      if (deliver) {
        await emailQuote(saved.id, email);
        setEmailed(email);
      }
      setQuote(saved);
      onSaved?.(saved);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy("");
    }
  };

  const emailExisting = async () => {
    if (!quote) return;
    setError("");
    setBusy("email");
    try {
      await emailQuote(quote.id, email);
      setEmailed(email);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-2 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-xl transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-start justify-between">
                  <Dialog.Title className="text-lg font-bold text-[#00062e]">
                    {quote
                      ? `Quote ${quote.reference}`
                      : revisingQuoteId
                      ? "Save a new version of this quote"
                      : "Get a quote"}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Close"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {quote ? (
                  <div className="mt-4 text-sm text-gray-700">
                    <p>
                      Your quote is saved. It holds until{" "}
                      <span className="font-medium">
                        {day(quote.validUntil)}
                      </span>{" "}
                      and comes to{" "}
                      <span className="font-medium">
                        {money(quote.totals.totalPrice, quote.totals.currency)}
                      </span>
                      {quote.includeShipping ? " including shipping" : ""}.
                      Convert to a purchase order or check out before then and
                      you keep these prices even if the catalog has gone up.
                    </p>
                    {emailed && (
                      <p className="mt-2 text-green-700">
                        Sent to {emailed} as a PDF.
                      </p>
                    )}
                    {!signedIn && (
                      <p className="mt-2 text-gray-600">
                        You are quoted as a guest, so this quote is kept against
                        this browser. Sign in and it moves to your account,
                        where clearing cookies cannot lose it.
                      </p>
                    )}
                    {error && <p className="mt-3 text-red-600">{error}</p>}
                    <div className="mt-5 flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        className="rounded-full bg-[#00062e] px-4 py-2 text-sm font-medium text-white hover:bg-slate-900"
                        onClick={() => void downloadQuotePdf(quote)}
                      >
                        Download the PDF
                      </button>
                      {quoting.emailPdf && !emailed && (
                        <button
                          type="button"
                          className="rounded-full border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                          onClick={() => void emailExisting()}
                          disabled={busy === "email"}
                        >
                          {busy === "email" ? "Sending..." : "Email it to me"}
                        </button>
                      )}
                      <Link
                        className="text-sm text-blue-700 hover:underline"
                        to="/quotes"
                        onClick={onClose}
                      >
                        See all my quotes
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 text-sm">
                    <p className="text-gray-600">
                      {revisingQuoteId
                        ? "Your changes are saved as a new version, dated and numbered. The version you already have stays readable, so a quote already with your approvers is not altered underneath them."
                        : `A dated, numbered quote for the material in your cart, priced for ${quoting.validityDays} days — the document to put through your own purchase approvals or load into your ERP. It is saved under My quotes, where you can reopen and change it, or turn it into a purchase order once it is approved.`}
                    </p>

                    {!signedIn && quoting.promptSignIn && !blockedAsGuest && (
                      <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-gray-700">
                        <Link
                          className="font-medium text-blue-700 hover:underline"
                          to="/signin"
                        >
                          Sign in first
                        </Link>{" "}
                        and the quote is kept against your account, so you can
                        find it and change it later from any browser. Carrying
                        on without signing in still gets you the quote — it is
                        held against this browser and listed under Guest.
                      </div>
                    )}
                    {blockedAsGuest && (
                      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-gray-700">
                        Quotes are kept against an account.{" "}
                        <Link
                          className="font-medium text-blue-700 hover:underline"
                          to="/signin"
                        >
                          Sign in
                        </Link>{" "}
                        to get one.
                      </div>
                    )}

                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <label className="block">
                        <span className="text-xs text-gray-600">Name</span>
                        <input
                          className={field}
                          name="quote-name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </label>
                      <label className="block">
                        <span className="text-xs text-gray-600">Company</span>
                        <input
                          className={field}
                          name="quote-company"
                          value={company}
                          onChange={(e) => setCompany(e.target.value)}
                        />
                      </label>
                      <label className="block">
                        <span className="text-xs text-gray-600">
                          Email the quote goes to
                        </span>
                        <input
                          className={field}
                          name="quote-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </label>
                      <label className="block">
                        <span className="text-xs text-gray-600">
                          Phone (optional)
                        </span>
                        <input
                          className={field}
                          name="quote-phone"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                        />
                      </label>
                    </div>

                    {error && <p className="mt-3 text-red-600">{error}</p>}

                    <div className="mt-5 flex flex-wrap items-center gap-3">
                      {quoting.emailPdf && (
                        <button
                          type="button"
                          className="rounded-full bg-[#00062e] px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50"
                          onClick={() => void save(true)}
                          disabled={Boolean(busy) || blockedAsGuest || !email}
                        >
                          {busy === "email"
                            ? "Preparing..."
                            : "Email it as a PDF"}
                        </button>
                      )}
                      {quoting.saveWithoutEmail && (
                        <button
                          type="button"
                          className="rounded-full border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                          onClick={() => void save(false)}
                          disabled={Boolean(busy) || blockedAsGuest}
                        >
                          {busy === "save"
                            ? "Saving..."
                            : "Save it without sending"}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
